import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";

const AUTH_IMG = "/hero-cv-comparison.png";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      await loginWithToken(data.token, data.user);
      navigate("/"); // RootRedirect routes to the right landing page per persona
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="absolute top-3 left-2 sm:top-4 sm:left-3 z-20 rounded-lg bg-white/80 backdrop-blur px-2 py-1 shadow-sm border border-zinc-200/60">
        <Logo imgClassName="h-5" />
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground mt-2 text-sm">Sign in to manage your CVs and applications.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider">Password</Label>
                <Link to="/forgot-password" className="text-xs text-accent font-medium hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" className="h-11" />
            </div>

            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}

            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 active:scale-[0.98] transition-transform">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button variant="outline" onClick={googleLogin} data-testid="google-login-button" className="w-full h-11 active:scale-[0.98] transition-transform">
            Continue with Google
          </Button>

          <p className="text-sm text-muted-foreground mt-8 text-center">
            Don't have an account?{" "}
            <Link to="/signup" className="text-accent font-medium hover:underline" data-testid="goto-signup-link">Sign up</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex items-center justify-center relative bg-zinc-50 border-l border-zinc-200 p-10">
        <img src={AUTH_IMG} alt="Standard CV versus AI-tailored CV comparison" className="max-h-full max-w-full object-contain scale-[1.15]" />
      </div>
    </div>
  );
}
